import {
	Form,
	FormField,
	FormSection,
	FormType,
} from '../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { VentasEnvironmentDefinition } from '../environment-variables';
import { VentasListsDefinition } from './list-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { FormRequestTypes } from '../../../../../common/components/reactive-form-builder/form-request';
import {
	FilteredValue,
	InputType,
} from '../../../../../common/components/reactive-form-builder/interfaces/input.interface';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { VentaModel } from 'src/app/models/ventas/venta.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import { list } from 'src/app/common/classes/request-builder';

export class VentasFormDefinition {
	public formType!: FormType;

	public env: VentasEnvironmentDefinition = new VentasEnvironmentDefinition();

	public lists: VentasListsDefinition = new VentasListsDefinition();

	public usuario = StorageManager.getUser();

	public cliente = StorageManager.getClient();

	public model: VentaModel = {} as VentaModel;

	public formFieldsSection!: FormSection<object>;

	public formFields!: Form<VentaModel>;

	public fieldNames: FormField<any>[] = [];

	public fieldLabels: string[] = [];

	constructor(type: FormRequestTypes, _model: Record<string, string>, table: (string[] | string[][])[] | null) {
		this.formType = +type;

		const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

		this.formFields = {
			name: 'ventas',
			header: action + ' ' + this.env.ventaName,
			type: this.formType,
			sections: this.getFormSections(),
			table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
			uploadButton: {
				isEnabled: false,
				label: '',
			},
			signed: false,
		};
	}

	public getFormFields(): FormField<any>[] {
		const formFields: FormField<any>[] = [
			{
				name: this.formType < 3 ? 'id_finca' : 'finca',
				vinculatedFields: undefined,
				label: 'Finca',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.fincas,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<FincasModel>): boolean => {
							const storedUser = StorageManager.getUser();
							const storedClient = StorageManager.getClient();
							const fincaClientId = this.parseNullableNumber(it.value?.id_cliente);

							if (!this.env.hasClients || !this.env.userCanSeeClientes) {
								if (storedUser.tipo === 'comunero') {
									const userClientId = this.parseNullableNumber(storedUser.id_cliente);
									const effectiveClientId = it.value?.nombre === 'PREFACIT' ? 9 : fincaClientId;

									return effectiveClientId === userClientId;
								}

								return true;
							}

							if (storedClient !== null) {
								const clientId = this.parseNullableNumber(storedClient.id);

								return fincaClientId === clientId;
							}

							return true;
						},
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 1,
			},
			{
				name: this.formType < 3 ? 'id_sector' : 'sector',
				vinculatedFields: undefined,
				label: this.env.sectorName,
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.sectores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<SectorModel>) => it.value?.id_finca === (this.lists.fincas.selected || {}).id,
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 2,
			},
			{
				name: this.formType < 3 ? 'ids_parcelas' : 'parcela',
				vinculatedFields: undefined,
				label: this.env.parcelasName,
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.parcelas,
					dropdownConfiguration: {
						summation: {
							summand: 'superficie_cultivada',
							name: this.env.tipoSuperficieParcelas,
							uds: this.env.textoSuperficie,
							format: (total: number) =>
								total.toLocaleString('es-ES', {
									useGrouping: true,
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						},
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<ParcelasModel>) => {
							if (it.value?.activo?.toString() !== '1') {
								return false;
							}

							const sectoresSeleccionados = this.lists.sectores.selected;
							const fincaSeleccionada = this.lists.fincas.selected;

							if (sectoresSeleccionados && +(sectoresSeleccionados.length ?? 0) > 0) {
								return sectoresSeleccionados.some((sector: SectorModel) => sector?.id === it.value?.id_sector);
							}

							return it.value.id_finca === fincaSeleccionada?.id;
						},
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: !['pyf', 'avafitogest'].includes(this.env.appName),
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 3,
			},
			{
				name: 'fecha',
				vinculatedFields: undefined,
				label: 'Fecha',
				input: { type: InputType.CALENDAR, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 4,
			},
			{
				name: 'albaran',
				vinculatedFields: undefined,
				label: 'Nº Albarán / Factura',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 5,
			},
			{
				name: 'lote',
				vinculatedFields: undefined,
				label: 'Nº Lote',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showVentasLote,
				},
				order: 6,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				vinculatedFields: undefined,
				label: this.env.appName === 'esparragosgranada' ? 'Cooperativa' : 'Comercio',
				input: {
					type: InputType.DROPDOWN_SEARCH,
					isEditable: true,
					data: this.lists.compradores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<Compradores>) => it.value?.tipo === 'Comercio',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible:
						this.env.showVentasComercio && this.env.appName !== 'esparragosgranada' && this.env.appName !== 'covidai',
				},
				order: 7,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				vinculatedFields: undefined,
				label: 'Clientes',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.clientes,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'covidai',
				},
				order: 8,
			},
			{
				name: this.formType < 3 ? 'id_comprador' : 'comprador',
				vinculatedFields: undefined,
				label: this.env.appName === 'esparragosgranada' ? 'Cooperativa' : 'Comercio',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.compradores,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<Compradores>) => it.value?.tipo === 'Comercio',
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showVentasComercio && this.env.appName === 'esparragosgranada',
				},
				order: 9,
			},
			{
				name: 'producto',
				vinculatedFields: undefined,
				label: 'Producto',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'pyf',
				},
				order: 10,
			},
			{
				name: this.formType < 3 ? 'id_variedad' : 'variedad',
				vinculatedFields: undefined,
				label: 'Variedad',
				input: {
					type: InputType.MULTISELECT_SEARCH,
					isEditable: true,
					data: this.lists.variedades,
					dropdownConfiguration: {
						valuePrimaryKey: 'id',
						filter: (it: FilteredValue<VisitasVariedadMesModel>) => {
							const seleccionadas = (this.lists.parcelas.selected as ParcelasModel[]) || [];
							if (!seleccionadas.length) return false;

							if (this.env.activateSIEX && !this.env.hasPlantas) {
								return seleccionadas.some((p) => p.id_variedad === it.value?.id);
							} else {
								return seleccionadas.some((p) => p.id_variedad_legacy === it.value?.id);
							}
						},
					},
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName !== 'pyf',
				},
				order: 11,
			},
			{
				name: 'cantidad',
				vinculatedFields: undefined,
				label: 'Cantidad Kg',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 12,
			},
			{
				name: 'precio',
				vinculatedFields: undefined,
				label: 'Precio',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 13,
			},
			{
				name: 'precio_kg',
				vinculatedFields: undefined,
				label:
					this.env.appName === 'fotoagricultura'
						? 'Precio / Kg'
						: this.env.appName === 'agrosol'
							? 'Importe Parcial'
							: 'Importe',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 14,
			},
			{
				name: 'descuento_porcentaje',
				vinculatedFields: undefined,
				label: 'Descuento %',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 15,
			},
			{
				name: 'descuento_euros',
				vinculatedFields: undefined,
				label: 'Descuento €',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 16,
			},
			{
				name: 'importe_total',
				vinculatedFields: undefined,
				label: 'Importe Total',
				input: { type: InputType.NUMERIC, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agrosol',
				},
				order: 17,
			},
			{
				name: 'matricula',
				vinculatedFields: undefined,
				label: 'Matricula',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'campanaragricola',
				},
				order: 17,
			},
			{
				name: 'estado',
				vinculatedFields: undefined,
				label: 'Estado',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: list(
						['...', null],
						['Esperando disponibilidad', 'Esperando disponibilidad'],
						['Esperando otro movimiento', 'Esperando otro movimiento'],
						['Reservado', 'Reservado'],
						['Parcialmente disponible', 'Parcialmente disponible'],
					),
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'momparler',
				},
				order: 17,
			},
		];

		this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			formFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	public getHiddenFields(): FormField<any>[] {
		const hiddenFields: FormField<any>[] = [
			{
				name: 'id',
				vinculatedFields: undefined,
				label: '',
				input: {
					type: InputType.HIDDEN,
					defaultValue: this.model.id,
					isEditable: false,
				},
				width: 2,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 1,
			},
			{
				name: 'id_cliente',
				vinculatedFields: undefined,
				label: '',
				input: {
					type: InputType.HIDDEN,
					defaultValue: this.cliente?.id !== null ? this.cliente?.id : this.lists.fincas.selected?.id_cliente,
					isEditable: false,
				},
				updateValue: () => (this.cliente?.id !== null ? this.cliente?.id : this.lists.fincas.selected?.id_cliente),
				width: 2,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.hasClients,
				},
				order: 2,
			},
			{
				name: 'id_usuario',
				vinculatedFields: undefined,
				label: '',
				input: {
					isEditable: false,
					defaultValue:
						this.env.applicationType === 'cropgest'
							? (this.cliente?.id_usuario ?? this.usuario.id)
							: (this.cliente?.id_usuario_asignado ?? this.model.id_usuario ?? this.usuario.id),
					type: InputType.HIDDEN,
				},
				updateValue: () =>
					this.env.applicationType === 'cropgest'
						? (this.cliente?.id_usuario ?? this.usuario.id)
						: (this.cliente?.id_usuario_asignado ?? this.model.id_usuario ?? this.usuario.id),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 3,
			},
		];

		this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			hiddenFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	private getFormSections(): FormSection<any>[] {
		const sections: FormSection<any>[] = [
			{
				name: '',
				width: 12,
				isVisible: true,
				fields: this.getFormFields(),
				order: 1,
			},
			{
				name: '',
				width: 12,
				isVisible: true,
				fields: this.getHiddenFields(),
				order: 2,
			},
		];

		return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	private parseNullableNumber(value: unknown): number | null {
		if (typeof value === 'number' && Number.isFinite(value)) {
			return value;
		}

		if (typeof value === 'string' && value.trim() !== '') {
			const parsedValue = Number(value);

			return Number.isFinite(parsedValue) ? parsedValue : null;
		}

		return null;
	}
}
